import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  User,
  UserCredential,
} from 'firebase/auth';
import { auth } from '../firebase';
import apiClient from './api';
import { getTenantFromHostname } from '../utils';

const requireAuth = () => {
  if (!auth) {
    throw new Error('Firebase Auth is not configured. Set REACT_APP_FIREBASE_* env vars.');
  }
  return auth;
};

type AuthProvider = 'google' | 'email';

/**
 * Custom error for tenant mismatch - user should be redirected to correct tenant
 */
export class TenantMismatchError extends Error {
  storedTenant: string;
  requestTenant: string;
  redirectUrl: string;

  constructor(storedTenant: string, requestTenant: string) {
    super(`This account is registered under '${storedTenant}'. Redirecting to correct tenant.`);
    this.name = 'TenantMismatchError';
    this.storedTenant = storedTenant;
    this.requestTenant = requestTenant;
    
    // Build redirect URL based on stored tenant
    const port = window.location.port ? `:${window.location.port}` : '';
    if (storedTenant === 'default') {
      this.redirectUrl = `http://aitutor.local${port}/`;
    } else {
      this.redirectUrl = `http://aitutor.${storedTenant}.local${port}/`;
    }
  }
}

/**
 * Register user with tenant on backend after Firebase authentication
 * Called during sign-in for immediate tenant validation
 * Note: The branding saga also handles registration for session restore scenarios
 * 
 * If TENANT_MISMATCH error occurs, signs out user and throws TenantMismatchError
 */
async function registerUserWithTenant(credential: UserCredential, provider: AuthProvider): Promise<void> {
  const tenant = getTenantFromHostname();
  const token = await credential.user.getIdToken();
  
  try {
    await apiClient.post('/auth/register', {
      firebaseUid: credential.user.uid,
      email: credential.user.email,
      displayName: credential.user.displayName,
      provider,
      tenant: tenant || 'default',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log('[Auth] User registered with backend:', { provider, tenant: tenant || 'default' });
  } catch (error: any) {
    const responseData = error?.response?.data;
    
    // Handle TENANT_MISMATCH - sign out and redirect
    if (responseData?.error === 'TENANT_MISMATCH') {
      console.error('[Auth] Tenant mismatch detected:', responseData);
      
      // Sign out from Firebase immediately
      if (auth) {
        await firebaseSignOut(auth);
      }
      
      // Throw special error with redirect info
      throw new TenantMismatchError(responseData.storedTenant, responseData.requestTenant);
    }
    
    // For other errors, log but don't block login
    console.warn('[Auth] Failed to register user with backend:', error);
  }
}

export const authService = {
  signInWithEmailPassword: async (email: string, password: string) => {
    const a = requireAuth();
    const credential = await signInWithEmailAndPassword(a, email, password);
    await registerUserWithTenant(credential, 'email');
    return credential;
  },

  signUpWithEmailPassword: async (email: string, password: string) => {
    const a = requireAuth();
    const credential = await createUserWithEmailAndPassword(a, email, password);
    await registerUserWithTenant(credential, 'email');
    return credential;
  },

  signInWithGoogle: async () => {
    const a = requireAuth();
    const googleProvider = new GoogleAuthProvider();
    const credential = await signInWithPopup(a, googleProvider);
    await registerUserWithTenant(credential, 'google');
    return credential;
  },

  signOut: async () => {
    const a = requireAuth();
    return firebaseSignOut(a);
  },

  getCurrentUser: (): User | null => {
    if (!auth) return null;
    return auth.currentUser;
  },

  getIdToken: async (forceRefresh = false): Promise<string | null> => {
    if (!auth?.currentUser) return null;
    return auth.currentUser.getIdToken(forceRefresh);
  },

  sendPasswordResetEmail: async (email: string) => {
    const a = requireAuth();
    return firebaseSendPasswordResetEmail(a, email);
  },
};
